// src/App.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Tone from "tone";

/* ---------- constants (keep outside component) ---------- */
const NOTE_NAMES = [
  "C",
  "C#",
  "D",
  "Eb",
  "E",
  "F",
  "F#",
  "G",
  "Ab",
  "A",
  "Bb",
  "B",
];
const DEGREE_BY_SEMITONE = {
  0: "1",
  1: "b2",
  2: "2",
  3: "b3",
  4: "3",
  5: "4",
  6: "b5",
  7: "5",
  8: "b6",
  9: "6",
  10: "b7",
  11: "7",
};
const DEGREE_TO_SEMITONE = Object.fromEntries(
  Object.entries(DEGREE_BY_SEMITONE).map(([k, v]) => [v, Number(k)])
);
const ORDERED_DEGREES = [
  "1",
  "b2",
  "2",
  "b3",
  "3",
  "4",
  "b5",
  "5",
  "b6",
  "6",
  "b7",
  "7",
];

const DEGREE_LABEL = {
  1: "1 / R",
  b2: "b2 / b9",
  2: "2 / 9",
  b3: "b3 / #9",
  4: "4 / 11",
  b5: "b5 / #11",
  b6: "b6 / b13",
  6: "6 / 13",
  // everything else shows as-is by default
};

const LEAD_IN_HITS = 4; // number of extra C5 hits before the chord (try 2–4)

function midiFrom(pc, octave) {
  return 12 * (octave + 1) + pc;
}
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

/* ======================================================== */
export default function App() {
  const DEFAULT_BPM = 50;

  // ---- React state/refs (hooks) ----
  const [isRunning, setIsRunning] = useState(false);
  const [lhOctave, setLhOctave] = useState(2);
  const [allowMajor, setAllowMajor] = useState(true);
  const [allowMinor, setAllowMinor] = useState(false);
  const [functionPool, setFunctionPool] = useState([
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
  ]);
  const [nextPressed, setNextPressed] = useState(false);

  const [current, setCurrent] = useState(null); // { pcRoot, quality, degree }
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [pressedQ, setPressedQ] = useState(null);

  // flash controls background color: null | "good" | "bad" | "demo"
  const [flash, setFlash] = useState(null);
  const flashRef = useRef(null);
  useEffect(() => {
    flashRef.current = flash;
  }, [flash]);

  // audio refs
  const synthRef = useRef(null); // polysynth (C5 + chords)
  const fxRef = useRef(null); // SFX synth
  const topLoopRef = useRef(null); // main C5 loop
  const demoTopLoopRef = useRef(null); // demo-only C5 loop
  const chordLoopRef = useRef(null);
  const heldChordRef = useRef([]); // sustained chord notes
  const isSequencingRef = useRef(false); // prevents overlap during sequences
  const currentRef = useRef(current);
  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  // fixed top note (C5)
  const topNoteMidi = useMemo(() => midiFrom(0, 5), []);

  // ---- helpers ----
  function getLHVoicing(pcRoot, quality) {
    const root = midiFrom(pcRoot, lhOctave);
    const fifth = root + 7;
    const tenth = root + (quality === "major" ? 16 : 15);
    return [root, fifth, tenth];
  }
  function computeFunction(pcRoot) {
    const interval = ((topNoteMidi % 12) - pcRoot + 12) % 12;
    return DEGREE_BY_SEMITONE[interval];
  }

  function strikeChord(pcRoot, quality, vel = 0.8) {
    if (!synthRef.current) return;
    const [r, f, t] = getLHVoicing(pcRoot, quality);
    const now = Tone.now();
    // small staggers feel nicer
    synthRef.current.triggerAttackRelease(
      Tone.Frequency(r, "midi"),
      "2n",
      now,
      vel
    );
    synthRef.current.triggerAttackRelease(
      Tone.Frequency(f, "midi"),
      "2n",
      now + 0.02,
      vel
    );
    synthRef.current.triggerAttackRelease(
      Tone.Frequency(t, "midi"),
      "2n",
      now + 0.04,
      vel
    );
  }

  function pickQuestion() {
    const qualities = [allowMajor && "major", allowMinor && "minor"].filter(
      Boolean
    );
    if (qualities.length === 0) {
      qualities.push("major");
      setAllowMajor(true);
    }
    const degree =
      functionPool[Math.floor(Math.random() * functionPool.length)] || "1";
    const k = DEGREE_TO_SEMITONE[degree];
    const pcRoot = (12 - k) % 12; // choose root so C becomes the selected degree
    const quality = qualities[Math.floor(Math.random() * qualities.length)];
    setCurrent({ pcRoot, quality, degree });
  }

  const PICKUP_NOTE_SEC = 0.22; // how long the single C5 lasts
  const PICKUP_GAP_SEC = 0.18; // silent gap after C5 before the chord

  async function presentQuestionLeadIn(opts = {}) {
    const { fromSequence = false } = opts;

    if (!fromSequence) {
      if (isSequencingRef.current) return;
      isSequencingRef.current = true;
    }

    // 1) Pick a new question (same logic as pickQuestion, but kept inline)
    const qualities = [allowMajor && "major", allowMinor && "minor"].filter(
      Boolean
    );
    if (qualities.length === 0) {
      qualities.push("major");
      setAllowMajor(true);
    }
    const degree =
      functionPool[Math.floor(Math.random() * functionPool.length)] || "1";
    const k = DEGREE_TO_SEMITONE[degree];
    const pcRoot = (12 - k) % 12;
    const quality = qualities[Math.floor(Math.random() * qualities.length)];
    const q = { pcRoot, quality, degree };
    setCurrent(q);

    // 2) Silence everything
    stopAllNotes();

    // ---- NEW: four-hit C5 lead-in ----
    const eighthSec = Tone.Time("8n").toSeconds();

    // ensure the main C5 loop exists
    if (!topLoopRef.current) {
      const loop = new Tone.Loop((time) => {
        synthRef.current?.triggerAttackRelease(
          Tone.Frequency(topNoteMidi, "midi"),
          "8n",
          time,
          0.9
        );
      }, "8n");
      topLoopRef.current = loop;
    }

    // hit #1 right now so we don't wait for the loop boundary
    const now = Tone.now();
    synthRef.current?.triggerAttackRelease(
      Tone.Frequency(topNoteMidi, "midi"),
      "8n",
      now,
      0.95
    );

    // start the loop so it gives us hits #2–#4
    topLoopRef.current.start(0);

    // wait for the remaining hits plus a tiny breath before the chord
    await sleep(((LEAD_IN_HITS - 1) * eighthSec + PICKUP_GAP_SEC) * 1000);
    // ---- end NEW ----

    // --- re-strike piano chord instead of holding ---
    strikeChord(q.pcRoot, q.quality);

    // re-strike the chord every half note so sustain never dies out
    const RETRIGGER_EVERY = "2n"; // try "1n" for less frequent, "4n" for more frequent
    chordLoopRef.current?.stop(0);
    chordLoopRef.current?.dispose();

    const chordLoop = new Tone.Loop((time) => {
      const [r, f, t] = getLHVoicing(q.pcRoot, q.quality);
      synthRef.current?.triggerAttackRelease(
        Tone.Frequency(r, "midi"),
        "2n",
        time,
        0.85
      );
      synthRef.current?.triggerAttackRelease(
        Tone.Frequency(f, "midi"),
        "2n",
        time + 0.02,
        0.85
      );
      synthRef.current?.triggerAttackRelease(
        Tone.Frequency(t, "midi"),
        "2n",
        time + 0.04,
        0.85
      );
    }, RETRIGGER_EVERY);

    chordLoop.start(Tone.Time(RETRIGGER_EVERY)); // first repeat after one interval
    chordLoopRef.current = chordLoop;

    if (!fromSequence) {
      isSequencingRef.current = false;
    }
  }

  function sustainCurrentChord() {
    if (!current || !synthRef.current) return;

    // release anything old just in case
    if (heldChordRef.current.length) {
      synthRef.current.triggerRelease(
        heldChordRef.current.map((n) => Tone.Frequency(n, "midi"))
      );
      heldChordRef.current = [];
    }

    // (re)hold the chord for the existing question
    const [r, f, t] = getLHVoicing(current.pcRoot, current.quality);
    const now = Tone.now();
    synthRef.current.triggerAttack(Tone.Frequency(r, "midi"), now, 0.7);
    synthRef.current.triggerAttack(Tone.Frequency(f, "midi"), now + 0.02, 0.7);
    synthRef.current.triggerAttack(Tone.Frequency(t, "midi"), now + 0.04, 0.7);
    heldChordRef.current = [r, f, t];
  }

  // ---- audio setup (piano) ----
  useEffect(() => {
    const reverb = new Tone.Reverb({ decay: 2.8, wet: 0.2 }).toDestination();

    const piano = new Tone.Sampler({
      urls: {
        C4: "C4.mp3",
        "D#4": "Ds4.mp3",
        "F#4": "Fs4.mp3",
        A4: "A4.mp3",
      },
      release: 1,
      baseUrl: "https://tonejs.github.io/audio/salamander/",
      onload: () => console.log("🎹 Piano samples loaded"),
    });

    // route piano through a touch of reverb so it’s clearly acoustic
    piano.connect(reverb);
    piano.volume.value = 4;

    // keep your SFX beeps as-is
    const fx = new Tone.Synth({
      oscillator: { type: "square" },
      envelope: { attack: 0.002, decay: 0.12, sustain: 0, release: 0.12 },
      volume: 0,
    }).toDestination();

    synthRef.current = piano;
    fxRef.current = fx;

    return () => {
      chordLoopRef.current?.stop(0);
      chordLoopRef.current?.dispose?.();
      chordLoopRef.current = null;

      topLoopRef.current?.dispose();
      demoTopLoopRef.current?.dispose();
      if (heldChordRef.current.length) {
        const now = Tone.now();
        synthRef.current?.triggerRelease(
          heldChordRef.current.map((n) => Tone.Frequency(n, "midi")),
          now
        );
        heldChordRef.current = [];
      }
      synthRef.current?.dispose();
      fx.dispose();
    };
  }, []);

  // sustain chord for current question
  useEffect(() => {
    if (!isRunning || !current || !synthRef.current || isSequencingRef.current)
      return;

    // release previous chord
    if (heldChordRef.current.length) {
      synthRef.current.triggerRelease(
        heldChordRef.current.map((n) => Tone.Frequency(n, "midi"))
      );
      heldChordRef.current = [];
    }
    // hold new chord
    const [r, f, t] = getLHVoicing(current.pcRoot, current.quality);
    const now = Tone.now();
    synthRef.current.triggerAttack(Tone.Frequency(r, "midi"), now, 0.7);
    synthRef.current.triggerAttack(Tone.Frequency(f, "midi"), now + 0.02, 0.7);
    synthRef.current.triggerAttack(Tone.Frequency(t, "midi"), now + 0.04, 0.7);
    heldChordRef.current = [r, f, t];
  }, [current, isRunning, lhOctave]);

  // ---- transport ----
  async function handleStart() {
    await Tone.start();
    await Tone.loaded();
    Tone.Transport.bpm.value = DEFAULT_BPM;
    Tone.Transport.start();

    if (!current) {
      // first time / no question yet → use the lead-in version
      await presentQuestionLeadIn();
    } else {
      // resume existing question (no lead-in)
      if (!topLoopRef.current) {
        const loop = new Tone.Loop((time) => {
          synthRef.current?.triggerAttackRelease(
            Tone.Frequency(topNoteMidi, "midi"),
            "8n",
            time,
            0.9
          );
        }, "8n");
        topLoopRef.current = loop;
      }
      topLoopRef.current.start(0);
      sustainCurrentChord();
    }

    setIsRunning(true);
  }
  function handleStop() {
    // pause the global clock
    Tone.Transport.pause();

    // hard-stop any notes/loops
    stopAllNotes();

    setIsRunning(false);
  }

  // ---- visual feedback ----
  function flashUI(kind, { sticky = false, duration = 450 } = {}) {
    setFlash(kind);
    if (navigator?.vibrate) navigator.vibrate(kind === "bad" ? 25 : 10);
    if (sticky) return; // don't auto-clear; we'll clear manually later
    setTimeout(() => {
      if (flashRef.current === kind) setFlash(null);
    }, duration);
  }

  // ---- SFX ----
  function sfxCorrect() {
    const now = Tone.now();
    fxRef.current?.triggerAttackRelease("A5", 0.1, now, 0.45);
    fxRef.current?.triggerAttackRelease("C6", 0.12, now + 0.12, 0.45);
  }
  function sfxWrong() {
    const now = Tone.now();
    fxRef.current?.triggerAttackRelease("E4", 0.1, now, 0.45);
    fxRef.current?.triggerAttackRelease("C4", 0.12, now + 0.12, 0.45);
  }

  // ---- utilities ----
  function stopAllNotes() {
    // stop any loops
    topLoopRef.current?.stop(0);
    demoTopLoopRef.current?.stop(0);

    // stop the repeating LH chord loop too
    chordLoopRef.current?.stop(0);
    chordLoopRef.current?.dispose?.();
    chordLoopRef.current = null;

    // cancel any scheduled events on the Transport (belt & suspenders)
    Tone.Transport.cancel(0);

    // release any held chord notes now
    if (heldChordRef.current.length) {
      const now = Tone.now();
      synthRef.current?.triggerRelease(
        heldChordRef.current.map((n) => Tone.Frequency(n, "midi")),
        now
      );
      heldChordRef.current = [];
    }

    // force-release anything else the polysynth might still be holding
    if (synthRef.current?.releaseAll) {
      synthRef.current.releaseAll();
    }
  }
  function startDemoTopLoop() {
    demoTopLoopRef.current?.dispose();
    const loop = new Tone.Loop((time) => {
      synthRef.current?.triggerAttackRelease(
        Tone.Frequency(topNoteMidi, "midi"),
        "8n",
        time,
        0.9
      );
    }, "8n");
    loop.start(0);
    demoTopLoopRef.current = loop;
  }

  // ---- sequences ----
  // wrong: stop -> pause -> wrong SFX -> pause -> demo (chord+C5, yellow) -> pause -> restore prompt
  async function runWrongSequence(chosenDegree) {
    if (!isRunning || isSequencingRef.current) return;
    isSequencingRef.current = true;
    flashUI("bad");

    // timings (tweak if you like)
    const pauseBeforeSfx = 500;
    const pauseAfterSfx = 500;
    const demoDuration = 3500; // ms
    const pauseAfterDemo = 900;

    stopAllNotes();

    await sleep(pauseBeforeSfx);
    sfxWrong();
    await sleep(pauseAfterSfx);

    // yellow during demo, and keep it until we clear
    flashUI("demo", { sticky: true });

    // figure out demo chord
    const k = DEGREE_TO_SEMITONE[chosenDegree];
    const demoRootPc = (12 - k) % 12;
    const colorMajor = new Set(["3", "6", "7"]);
    const colorMinor = new Set(["b3", "b6", "b7"]);
    let demoQuality = currentRef.current?.quality || "major";
    if (colorMajor.has(chosenDegree)) demoQuality = "major";
    if (colorMinor.has(chosenDegree)) demoQuality = "minor";

    // play demo chord + C5
    const [r, f, t] = getLHVoicing(demoRootPc, demoQuality);
    const now = Tone.now();
    synthRef.current?.triggerAttack(Tone.Frequency(r, "midi"), now, 0.8);
    synthRef.current?.triggerAttack(Tone.Frequency(f, "midi"), now + 0.02, 0.8);
    synthRef.current?.triggerAttack(Tone.Frequency(t, "midi"), now + 0.04, 0.8);
    heldChordRef.current = [r, f, t];

    startDemoTopLoop();
    await sleep(demoDuration);

    // stop demo
    demoTopLoopRef.current?.stop(0);
    if (heldChordRef.current.length) {
      synthRef.current?.triggerRelease(
        heldChordRef.current.map((n) => Tone.Frequency(n, "midi"))
      );
      heldChordRef.current = [];
    }

    await sleep(pauseAfterDemo);
    setFlash(null); // clear yellow

    // restore original prompt chord and main C5
    const cur = currentRef.current;
    if (cur) {
      const [r0, f0, t0] = getLHVoicing(cur.pcRoot, cur.quality);
      const tNow = Tone.now();
      synthRef.current?.triggerAttack(Tone.Frequency(r0, "midi"), tNow, 0.7);
      synthRef.current?.triggerAttack(
        Tone.Frequency(f0, "midi"),
        tNow + 0.02,
        0.7
      );
      synthRef.current?.triggerAttack(
        Tone.Frequency(t0, "midi"),
        tNow + 0.04,
        0.7
      );
      heldChordRef.current = [r0, f0, t0];
      topLoopRef.current?.start(0);
    }

    isSequencingRef.current = false;
  }

  // correct: stop -> pause -> correct SFX -> pause -> next question (resume)
  async function runCorrectSequence() {
    if (!isRunning || isSequencingRef.current) return;
    isSequencingRef.current = true;
    flashUI("good");

    const pauseBeforeSfx = 350;
    const pauseAfterSfx = 350;

    stopAllNotes();
    await sleep(pauseBeforeSfx);
    sfxCorrect();
    await sleep(pauseAfterSfx);

    await presentQuestionLeadIn({ fromSequence: true });
    isSequencingRef.current = false;
  }

  // ---- answer handling ----
  function answer(choice) {
    if (!current || isSequencingRef.current) return;
    const correct = computeFunction(current.pcRoot) === choice;
    setScore((s) => ({
      correct: s.correct + (correct ? 1 : 0),
      total: s.total + 1,
    }));
    if (correct) runCorrectSequence();
    else runWrongSequence(choice);
  }

  function next() {
    if (isSequencingRef.current) return;
    pickQuestion();
  }

  function toggleDegree(d) {
    setFunctionPool((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
    );
  }

  const qualitySummary =
    `${allowMajor ? "Major" : ""}${allowMajor && allowMinor ? " + " : ""}${
      allowMinor ? "Minor" : ""
    }` || "Major";

  // ---- UI ----
  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          flash === "good"
            ? "#047857"
            : flash === "bad"
            ? "#8b1c1c"
            : flash === "demo"
            ? "#4d4210" // yellow during demo
            : "#0a0a0a",
        color: "#fafafa",
        padding: 16,
        transition: "background 180ms ease",
      }}
    >
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <h1 style={{ fontSize: 24, marginBottom: 8 }}>Name That Function!</h1>
        <div style={{ opacity: 0.8, fontSize: 14, marginBottom: 16 }}>
          Top note: C5 • Chords: ({qualitySummary})
        </div>

        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
          }}
        >
          <div style={{ padding: 12, borderRadius: 12, background: "#151515" }}>
            <h2 style={{ fontSize: 16, marginBottom: 8 }}>Transport</h2>
            {!isRunning ? (
              <button onClick={handleStart} style={btn("#166534", "#062e26")}>
                Start
              </button>
            ) : (
              <button onClick={handleStop} style={btn("#f43f5e", "#30070e")}>
                Stop
              </button>
            )}
            <button
              onClick={() => {
                setNextPressed(true);
                isRunning ? presentQuestionLeadIn() : handleStart();
                setTimeout(() => setNextPressed(false), 180);
              }}
              onMouseDown={() => setNextPressed(true)}
              onMouseUp={() => setNextPressed(false)}
              onMouseLeave={() => setNextPressed(false)}
              onTouchStart={() => setNextPressed(true)}
              onTouchEnd={() => setNextPressed(false)}
              style={{
                ...btn(nextPressed ? "#3a3a3a" : "#2a2a2a", "#111", 12), // lighter gray when pressed
                transition: "background-color 50ms ease",
              }}
            >
              Next Chord
            </button>
          </div>

          <div style={{ padding: 12, borderRadius: 12, background: "#151515" }}>
            <h2 style={{ fontSize: 16, marginBottom: 8 }}>Sound</h2>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 13 }}>Chord Register</span>
              <select
                value={lhOctave}
                onChange={(e) => setLhOctave(parseInt(e.target.value))}
                style={sel()}
              >
                <option value={2}>Low</option>
                <option value={3}>High</option>
              </select>
            </div>
            <div style={{ marginTop: 12 }}>
              <label
                style={{ fontSize: 13, display: "block", marginBottom: 6 }}
              >
                Chord Qualities
              </label>
              <label
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 6,
                }}
              >
                <input
                  type="checkbox"
                  checked={allowMajor}
                  onChange={() => setAllowMajor((v) => !v)}
                />{" "}
                Major
              </label>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={allowMinor}
                  onChange={() => setAllowMinor((v) => !v)}
                />{" "}
                Minor
              </label>
            </div>
          </div>

          <div style={{ padding: 12, borderRadius: 12, background: "#151515" }}>
            <h2 style={{ fontSize: 16, marginBottom: 8 }}>
              Answer Pool (functions of C)
            </h2>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {ORDERED_DEGREES.map((d) => (
                <button
                  key={d}
                  onClick={() => toggleDegree(d)}
                  style={{
                    ...btn(
                      functionPool.includes(d) ? "#60a5fa" : "#2a2a2a",
                      "#111",
                      10
                    ),
                    color: functionPool.includes(d) ? "#081b16" : "#fff",
                  }}
                >
                  {DEGREE_LABEL[d] || d}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
              Selected degrees (highlighted) are both your possible questions
              and the answer choices.
            </div>
          </div>
        </div>

        <div
          style={{
            padding: 12,
            borderRadius: 12,
            background: "#151515",
            marginTop: 12,
          }}
        >
          <h2 style={{ fontSize: 16, marginBottom: 8 }}>Question</h2>
          <div style={{ fontSize: 14, opacity: 0.85, marginBottom: 8 }}>
            Top note is fixed: <b>C5</b>. What function is it over the sustained
            chord below?
            {current && (
              <span
                style={{
                  marginLeft: 8,
                  padding: "2px 6px",
                  background: "#1f1f1f",
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                {allowMajor && allowMinor
                  ? current.quality
                  : allowMajor
                  ? "major"
                  : "minor"}{" "}
                chord
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {ORDERED_DEGREES.filter((d) => functionPool.includes(d)).map(
              (d) => (
                <button
                  key={d}
                  onClick={() => answer(d)}
                  onMouseDown={() => setPressedQ(d)}
                  onMouseUp={() => setPressedQ(null)}
                  onMouseLeave={() => setPressedQ(null)}
                  onTouchStart={() => setPressedQ(d)}
                  onTouchEnd={() => setPressedQ(null)}
                  style={{
                    ...btn(
                      pressedQ === d ? "#ea580c" : "#f97316",
                      "#7c2d12",
                      16
                    ), // orange + darker when pressed
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                >
                  {DEGREE_LABEL[d] || d}
                </button>
              )
            )}
          </div>
        </div>

        <div
          style={{
            padding: 12,
            borderRadius: 12,
            background: "#151515",
            marginTop: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Score</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>
              {score.correct} / {score.total}
            </div>
          </div>
          <button
            onClick={() => setScore({ correct: 0, total: 0 })}
            style={btn("#2a2a2a", "#111", 12)}
          >
            Reset score
          </button>
        </div>

        <div
          style={{
            textAlign: "center",
            fontSize: 12,
            opacity: 0.6,
            marginTop: 8,
            maxWidth: "100%", // keeps it from stretching edge-to-edge
            marginLeft: "auto", // centers horizontally
            marginRight: "auto", // centers horizontally
            wordWrap: "break-word", // ensures no overflow
            lineHeight: 1.4, // makes it easier to read
          }}
        >
          If you don’t hear audio, click inside the preview and press Start
          (browsers require a user gesture). On phones, make sure your mute
          switch is off and volume is up.
        </div>
      </div>
    </div>
  );
}

/* ---------- tiny style helpers ---------- */
function btn(bg, shadowBg, pad = 10) {
  return {
    marginRight: 8,
    padding: `${pad}px 14px`,
    borderRadius: 14,
    background: bg,
    color: "#fff",
    border: "none",
    cursor: "pointer",
    boxShadow: `0 0 0 1px ${shadowBg} inset`,
  };
}
function sel() {
  return {
    background: "#1f1f1f",
    color: "#fff",
    borderRadius: 10,
    padding: "4px 8px",
    border: "1px solid #2a2a2a",
  };
}
